/**
 * `sharedLinkDto.ts` — the shared-link row that backs project sharing.
 *
 * Dynamo addressing (`DynamoClient.cs` key guide):
 *   pk = `"{customerId}:sharedlinkId"`  (literal suffix — lowercase `l`,
 *                                        capital `I`; do NOT "fix" it)
 *   sk = the bare link id (no prefix)
 *
 * Written by `ToSharedLinkItem` as a full `PutItem` (overwrite semantics —
 * the id is deterministic per project+email, so re-inviting the same person
 * rewrites their row rather than adding one). `ProjectName` is the one attr
 * NOT written on create: only `UpdateSharedLinks` sets it, denormalizing the
 * name onto every link row when the project is renamed.
 */

export interface SharedLinkDto {
  readonly ID: string;
  /** {@link DEEPLINK_TYPE} int. */
  readonly LinkType?: number;
  /** Owning account of the SHARED project (not the sharee's account). */
  readonly CustomerID?: string;
  readonly ProjectID?: string;
  readonly SceneID?: string;
  readonly AnnotationID?: string;
  /**
   * Lowercase invitee email for invites, or the sentinel `"internal"` for
   * copy-links. Rows with an empty email are dropped from every share list.
   */
  readonly SharedWithEmail?: string;
  readonly ProjectAuthor?: string;
  /** {@link SHARED_USER_ROLE} int. Absent on legacy rows → Editor. */
  readonly UserRole?: number;
  /** {@link SHARED_LINK_STATE} flags. Absent on legacy rows → Visited. */
  readonly State?: number;
  /** Denormalized on rename only. */
  readonly ProjectName?: string;

  readonly [extra: string]: unknown;
}

/** Unity `DeeplinkType` (`DeepLinkGenerator.cs:11-18`). */
export const DEEPLINK_TYPE = {
  AnnotationLink: 1,
  SketchLink: 2,
  /** Email invites use SceneLink, NOT ProjectLink. */
  SceneLink: 3,
  /** Copy-link uses ProjectLink. */
  ProjectLink: 4,
  MeasurementLink: 5,
} as const;

/** Unity `SharedUserRole` (`SharedUsersModel.cs:32-41`). */
export const SHARED_USER_ROLE = {
  Owner: 0,
  Editor: 1,
  Commenter: 2,
  Viewer: 3,
  NoAccess: 4,
  /** Obsolete upstream — always downgraded to Commenter on read. */
  Presenter: 5,
} as const;

/** Unity `SharedLinkState` [Flags] (`SharedLinkData.cs:194-202`). */
export const SHARED_LINK_STATE = {
  Default: 0,
  Visited: 1 << 0,
  Downgraded: 1 << 1,
  DowngradeNotified: 1 << 2,
  DowngradeNotifiedOwner: 1 << 3,
} as const;

/**
 * Unity `AccountRole` — the account LICENSE role, distinct from
 * `SharedUserRole` (`UserDataExt.cs:11-18`). GraphQL uses the string names.
 */
export const ACCOUNT_ROLE = {
  Reader: 1,
  Writer: 2,
  Commenter: 4,
  /** Obsolete upstream. */
  Presenter: 8,
} as const;

/** Sentinel email on copy-link rows; filtered out of every roster. */
export const INTERNAL_SHARE_EMAIL = "internal";

/**
 * Read-side defaults (`ToSharedLinkData`): a legacy row with no `UserRole`
 * is an Editor share, and one with no `State` is treated as already visited.
 * Obsolete `Presenter` always reads as `Commenter`.
 */
export function sharedLinkUserRole(link: SharedLinkDto): number {
  const raw = typeof link.UserRole === "number" ? link.UserRole : SHARED_USER_ROLE.Editor;
  return raw === SHARED_USER_ROLE.Presenter ? SHARED_USER_ROLE.Commenter : raw;
}

export function sharedLinkState(link: SharedLinkDto): number {
  return typeof link.State === "number" ? link.State : SHARED_LINK_STATE.Visited;
}

export function isSharedLinkVisited(link: SharedLinkDto): boolean {
  return (sharedLinkState(link) & SHARED_LINK_STATE.Visited) !== 0;
}

/**
 * Rows that represent a real person's access to a project — Unity's roster
 * filter (`SharedUsersModel.GetProjectShares`): Project/Scene link types
 * only, a real email (not the `"internal"` copy-link sentinel), and not
 * revoked to `NoAccess`.
 */
export function isRosterShareLink(link: SharedLinkDto): boolean {
  const type = link.LinkType ?? 0;
  if (type !== DEEPLINK_TYPE.ProjectLink && type !== DEEPLINK_TYPE.SceneLink) return false;
  const email = link.SharedWithEmail ?? "";
  if (!email || email === INTERNAL_SHARE_EMAIL) return false;
  return sharedLinkUserRole(link) !== SHARED_USER_ROLE.NoAccess;
}

/**
 * `AccountRole` → `SharedUserRole` (`UserRolePermissions.cs:9-31`).
 * Reader→Viewer, Writer→Editor, Commenter→Commenter, anything else→Viewer.
 */
export function accountRoleToSharedUserRole(accountRole: number): number {
  switch (accountRole) {
    case ACCOUNT_ROLE.Writer: return SHARED_USER_ROLE.Editor;
    case ACCOUNT_ROLE.Commenter: return SHARED_USER_ROLE.Commenter;
    case ACCOUNT_ROLE.Reader:
    default: return SHARED_USER_ROLE.Viewer;
  }
}

/** `UserRolePermissions.CanShareProject()` — Owner or Editor may share. */
export function canShareProject(role: number): boolean {
  return role === SHARED_USER_ROLE.Owner || role === SHARED_USER_ROLE.Editor;
}

/** Human label for a role (Unity uses the enum `ToString` names). */
export function sharedUserRoleLabel(role: number): string {
  switch (role) {
    case SHARED_USER_ROLE.Owner: return "Owner";
    case SHARED_USER_ROLE.Editor: return "Editor";
    case SHARED_USER_ROLE.Commenter: return "Commenter";
    case SHARED_USER_ROLE.Viewer: return "Viewer";
    case SHARED_USER_ROLE.NoAccess: return "No access";
    default: return "Viewer";
  }
}
